import React, { useEffect, useRef, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { onAuthStateChanged } from "firebase/auth";
import { Row, Spinner } from "reactstrap";
import { auth, functions, httpsCallable } from "../../firebase";
import { fetchUserData, selectUser } from "features/userSlice";
import {
  getGoogleDriveRedirectUri,
  notifyGoogleDriveAuthOpener,
} from "utils/googleDriveUtils";

export default function GoogleDriveCallback() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const exchanged = useRef(false);
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const error = params.get("error");
    const state = params.get("state");

    if (error) {
      const message = `Google declined authorization: ${error}`;
      notifyGoogleDriveAuthOpener({ success: false, error: message });
      setErrorMsg(message);
      setStatus("error");
      return undefined;
    }

    if (!code || !state) {
      const message = "Google did not return the information needed to complete the connection.";
      notifyGoogleDriveAuthOpener({ success: false, error: message });
      setErrorMsg(message);
      setStatus("error");
      return undefined;
    }

    const exchange = async (firebaseUser) => {
      if (exchanged.current) return;
      exchanged.current = true;

      try {
        const exchangeFn = httpsCallable(functions, "googleDriveExchangeToken");

        await exchangeFn({
          code,
          state,
          redirectUri: getGoogleDriveRedirectUri(),
        });

        const uid = firebaseUser?.uid || user?.uid;
        if (uid) {
          await dispatch(fetchUserData(uid));
        }

        if (notifyGoogleDriveAuthOpener({ success: true })) {
          setStatus("success");
        } else {
          history.replace("/settings#google-drive");
        }
      } catch (err) {
        console.error("[GoogleDriveCallback] googleDriveExchangeToken error:", err);
        exchanged.current = false;
        const message = err.message || "Failed to connect Google Drive. Please try again.";
        notifyGoogleDriveAuthOpener({ success: false, error: message });
        setErrorMsg(message);
        setStatus("error");
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        clearTimeout(authTimeout);
        unsubscribe();
        exchange(firebaseUser);
      }
    });

    const authTimeout = setTimeout(() => {
      unsubscribe();
      setErrorMsg(
        "Could not restore your session. For local testing, make sure you log in at http://127.0.0.1:3000 before connecting Google Drive."
      );
      setStatus("error");
    }, 8000);

    return () => {
      clearTimeout(authTimeout);
      unsubscribe();
    };
  }, [dispatch, history, location.search, user?.uid]);

  if (status === "loading") {
    return (
      <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", flexDirection: "column", gap: "16px" }}>
        <Spinner style={{ height: "3rem", width: "3rem" }} color="info" />
        <p style={{ color: "#004aad", fontWeight: "bold" }}>Connecting your Google Drive account...</p>
      </Row>
    );
  }

  if (status === "success") {
    return (
      <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", flexDirection: "column", gap: "12px", padding: "20px" }}>
        <i className="fa fa-check-circle" style={{ fontSize: "48px", color: "#00a86b" }} />
        <h4 style={{ color: "#004aad", fontWeight: "bold" }}>Google Drive Connected</h4>
        <p style={{ color: "#666", textAlign: "center" }}>You can close this window and return to Lesson Launchpad.</p>
      </Row>
    );
  }

  return (
    <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", flexDirection: "column", gap: "12px", padding: "20px" }}>
      <i className="fa fa-exclamation-circle" style={{ fontSize: "48px", color: "#e74c3c" }} />
      <h4 style={{ color: "#004aad", fontWeight: "bold" }}>Google Drive Connection Failed</h4>
      <p style={{ color: "#666", textAlign: "center", maxWidth: "420px" }}>{errorMsg}</p>
      <button
        onClick={() => history.replace("/settings#google-drive")}
        style={{ marginTop: "8px", padding: "10px 24px", borderRadius: "30px", backgroundColor: "#00c4cc", color: "white", border: "none", cursor: "pointer", fontWeight: "bold" }}
      >
        Back to Settings
      </button>
    </Row>
  );
}
